/**
 * The one status pill for a sync run, shared by the list and the run-detail screens.
 *
 * Deliberately NOT a port of rudder-webapp's `components/common/syncStatusBadge/` — that badge
 * speaks a different, nine-state vocabulary (`SyncConfigStatus`), is used by the health dashboard
 * and the generic RETL screens, and nothing under `dataGraphAudiences` imports it. Porting it would
 * put two contradictory status vocabularies in one product. This maps the states a *run* actually
 * renders in (see `lib/run-view.ts`'s `runStatus`) onto the design system's `Badge` variants.
 */
import { Badge } from '@/_shared/components/ui/badge';

import { RUN_STATUS_LABEL, runStatus } from '../lib/run-view';
import type { RawSyncRun, RunStatus } from '../types';

type BadgeVariant = 'success' | 'warning' | 'error' | 'info' | 'outline';

const STATUS_VARIANT: Record<RunStatus, BadgeVariant> = {
  completed: 'success',
  completedWithFailures: 'warning',
  failed: 'error',
  running: 'info',
  // Deliberately NOT a severity tint. `outline` is the Badge primitive's one variant that carries no
  // success/warning/error/info color at all (`border-border text-foreground`), which is what "we do
  // not recognise this status" actually is — neither good news nor an alarm, just an unknown. It must
  // not read `success` (Task 7's whole point) and it must not borrow `warning`/`error` either, since
  // those already mean something specific this case cannot claim to know.
  unknown: 'outline',
};

interface SyncStatusBadgeProps {
  run: RawSyncRun;
  /** Tinted border, for a badge sitting on a card rather than in a dense table cell. */
  bordered?: boolean;
  className?: string;
}

export function SyncStatusBadge({ run, bordered = false, className }: SyncStatusBadgeProps): React.ReactElement {
  const status = runStatus(run);
  const variant = STATUS_VARIANT[status];
  // The border is the CALLER's call, not this component's. It briefly forced one on every `info`
  // badge, to fix a real collision — `--info-bg` and `--muted` are both `--grey-50`, so a Running
  // badge vanished into a selected row. But the fix belonged where that background exists: forcing it
  // here put a border on every `info` badge in the app, including the activations list, which has no
  // selected-row fill to survive. `RunsPanel`'s run table passes `bordered`; nothing else needs to.
  return (
    <Badge variant={variant} bordered={bordered} className={className}>
      {RUN_STATUS_LABEL[status]}
    </Badge>
  );
}
